import React from 'react';

/**
 * SectionedDialog — two-pane category dialog (admin-sectioned-dialogs slug,
 * ADR 2026-06-24). Replaces the long-scroll / "Next"-wizard provisioning
 * modals with a category list (left) + detail pane (right) and a persistent
 * footer. Purely presentational: the owning dialog keeps all form state and
 * hands us a category array with a few derived flags; we never interpret WHY
 * a category is shown or incomplete.
 *
 * Behaviors owned here (mapped to the requirements):
 *  1. The list GROWS/SHRINKS — only categories with `applicable !== false`
 *     render. Dependencies are expressed by toggling `applicable` (a section
 *     appears once its prerequisite is met) — there's no separate "locked" state.
 *  2. "Next" order becomes top-to-bottom category order (caller-supplied).
 *  3. FREE NAVIGATION — every visible category is clickable.
 *  4. NO dismiss on outside-click or Esc — the only exit is the Cancel button.
 *  5. Cancel + primary ALWAYS visible; the primary is enabled only when every
 *     applicable required category is complete (and no category reports an error).
 *
 * Result phase: when `result` is set the two panes are replaced by it and the
 * footer collapses to a single Close (used by dialogs that reveal credentials /
 * an installer build / a download after submit).
 *
 * Category shape:
 *   {
 *     id, label,
 *     description?,  // optional one-line blurb shown in the detail header
 *     applicable?,   // default true — in the list at all (req 1)
 *     complete?,     // all required fields filled → feeds validity (req 5)
 *     hasRequired?,  // default false — does this category gate the primary? (req 5)
 *     error?,        // optional dialog-level blocker string for this category
 *     render,        // () => detail-pane JSX
 *   }
 *
 * Props: see destructuring below.
 */
export default function SectionedDialog({
  title,
  // aveq-admin-ui-tweaks: `title` may be a JSX node (e.g. a styled station name).
  // `titleText` is the plain-string fallback for the dialog's aria-label in that case.
  titleText,
  categories,
  activeId,
  onSelect,
  primaryLabel = 'Save',
  submitting = false,
  submittingLabel,
  onPrimary,
  onCancel,
  cancelLabel = 'Cancel',
  error = null,
  result = null,
  resultFooter = null,
  onClose,
  width = 720,
  height = 600,
}) {
  const visible = categories.filter((c) => c.applicable !== false);
  const active = visible.find((c) => c.id === activeId) || visible[0];
  const inResult = result != null;

  // A required category blocks the primary until it's complete; any
  // category-level error also blocks. (req 5)
  const blockers = visible.filter((c) => c.hasRequired && !c.complete);
  const anyError = visible.some((c) => c.error);
  const primaryEnabled = !submitting && blockers.length === 0 && !anyError;

  return (
    // No onClick on the overlay → outside-click does not dismiss (req 4).
    <div style={overlayStyle}>
      {/* Fixed height while navigating categories so the header + footer never
          shift; the detail pane scrolls only when a category overflows. The
          result phase is left to size to its own content (a one-time swap, not
          navigation). `maxHeight` still caps it on short viewports. */}
      <div
        style={{ ...modalStyle, width, height: inResult ? undefined : height }}
        role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : titleText}
      >
        <h2 style={titleStyle}>{title}</h2>

        {inResult ? (
          <div style={resultPaneStyle}>{result}</div>
        ) : (
          <div style={bodyStyle}>
            {/* Left: category list — grows/shrinks with `applicable` (req 1). */}
            <nav style={listStyle} aria-label="Sections">
              {visible.map((c) => {
                const isActive = active && c.id === active.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => onSelect && onSelect(c.id)}
                    style={{
                      ...listItemStyle,
                      ...(isActive ? listItemActiveStyle : null),
                    }}
                  >
                    <span style={statusGlyphWrap}>{statusGlyph(c)}</span>
                    <span style={{ flex: 1 }}>{c.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Right: detail pane — a FIXED header (label + optional
                description) above a scrolling content area. */}
            <section style={detailStyle}>
              {active && (
                <div style={detailHeaderStyle}>
                  <div style={detailTitleStyle}>{active.label}</div>
                  {active.description && (
                    <div style={detailDescStyle}>{active.description}</div>
                  )}
                </div>
              )}
              <div style={detailContentStyle}>
                {active ? active.render() : null}
              </div>
            </section>
          </div>
        )}

        {error && <div style={errorStyle}>{error}</div>}

        {/* Footer — always visible (req 5). */}
        <div style={footerStyle}>
          {inResult ? (
            resultFooter || (
              <button type="button" onClick={onClose || onCancel} style={buttonPrimaryStyle}>
                Close
              </button>
            )
          ) : onPrimary ? (
            <>
              <button type="button" onClick={onCancel} style={buttonSecondaryStyle}>
                {cancelLabel}
              </button>
              <button
                type="button"
                onClick={onPrimary}
                disabled={!primaryEnabled}
                style={primaryEnabled ? buttonPrimaryStyle : buttonDisabledStyle}
                title={!primaryEnabled && blockers.length
                  ? `Complete: ${blockers.map((b) => b.label).join(', ')}`
                  : undefined}
              >
                {submitting ? (submittingLabel || 'Saving…') : primaryLabel}
              </button>
            </>
          ) : (
            // Read-only viewer (no onPrimary): just a single Close button.
            <button type="button" onClick={onCancel} style={buttonPrimaryStyle}>
              {cancelLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Per-category list affordance: an attention "!" when a category reports an
// error, and an amber dot ONLY for a required category that's still missing
// input. Satisfied/optional categories show nothing (a transparent placeholder
// keeps labels aligned) — we don't mark "done".
function statusGlyph(c) {
  if (c.error) return <span style={{ color: '#c00' }} title="Needs attention">!</span>;
  if (c.hasRequired && !c.complete) return <span style={{ color: '#e0a000' }} title="Required">●</span>;
  return <span style={{ color: 'transparent' }}>●</span>;
}

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const modalStyle = {
  background: '#fff', padding: 20, borderRadius: 6, maxWidth: '95vw', maxHeight: '88vh',
  display: 'flex', flexDirection: 'column',
  boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};
const titleStyle = { margin: '0 0 12px', fontSize: '1.1rem' };
// minHeight:0 lets the flex children own their own scroll instead of growing
// the modal past maxHeight.
const bodyStyle = { display: 'flex', gap: 16, flex: 1, minHeight: 0 };
const listStyle = {
  width: 190, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2,
  overflowY: 'auto', borderRight: '1px solid #eee', paddingRight: 8,
};
const listItemStyle = {
  display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
  padding: '8px 10px', border: 'none', background: 'transparent', borderRadius: 4,
  cursor: 'pointer', fontSize: '0.85rem', color: '#333', fontFamily: 'inherit',
};
const listItemActiveStyle = { background: '#eef5f0', color: '#2f6b48', fontWeight: 600 };
const statusGlyphWrap = {
  width: 16, flexShrink: 0, display: 'inline-flex', justifyContent: 'center',
  fontSize: '0.8rem',
};
const detailStyle = { flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' };
const detailHeaderStyle = { flexShrink: 0, marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid #eee' };
const detailTitleStyle = { fontSize: '0.95rem', fontWeight: 600, color: '#2f6b48' };
const detailDescStyle = { fontSize: '0.78rem', color: '#888', marginTop: 4, lineHeight: 1.4 };
const detailContentStyle = { flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 4 };
const resultPaneStyle = { flex: 1, minHeight: 0, overflowY: 'auto' };
const errorStyle = { color: '#c00', fontSize: '0.85rem', marginTop: 12 };
const footerStyle = {
  display: 'flex', gap: 8, justifyContent: 'flex-end',
  marginTop: 16, paddingTop: 12, borderTop: '1px solid #eee',
};
const buttonPrimaryStyle = {
  padding: '8px 16px', background: '#5fa67a', color: '#fff', border: 'none',
  borderRadius: 4, fontSize: '0.85rem', cursor: 'pointer',
};
const buttonDisabledStyle = { ...buttonPrimaryStyle, background: '#b8d4c5', cursor: 'not-allowed' };
const buttonSecondaryStyle = {
  padding: '8px 16px', background: 'transparent', color: '#5fa67a',
  border: '1px solid #5fa67a', borderRadius: 4, fontSize: '0.85rem', cursor: 'pointer',
};
